// My Garage — loads the Parts Finder React app in-place on the storefront's
// My Garage page and routes it to /my-garage.
//
// COMPLEX widget: it boots the full React bundle by asking a hidden same-origin
// iframe (my-garage-iframe.html) for the current hashed asset URLs, then injects
// those <link>/<script> tags. That iframe/postMessage protocol is the widget's
// own specialised machinery and is preserved verbatim here — only the generic
// base-URL / apiKey resolvers were swapped for the shared bundle config.
//
// Config (read from the TARGET element, not the script tag):
//   [data-parts-garage] — the container the React app mounts into.
//
// NOTE: pairs with app/public/js/my-garage.js (the iframe-side script served as
// my-garage-iframe.html); its GET_ASSET_URLS / *_VEHICLE message contract must
// stay in sync with the messages posted below.

import { getAppBaseUrl, getApiKey } from '../shared/config.js';

var _appBaseUrl = '';
var _loaderId = 'my-garage-loader';
var _communicationIframe = null;
var _assetsLoaded = false;
var _rootContainer = null;
var _iframeReady = false;
var _iframeReadyCallbacks = [];
var _assetUrlsPromise = null;

var Render = {
	Initialize: function (container) {
		console.log('MyGarageEmbed: Initializing...');
		console.log('MyGarageEmbed: Container found:', container);

		// Clear any existing content
		container.innerHTML = '';

		// Calculate top offset to account for parent site's header/menu
		var headerOffset = 0;
		var baseOffset = 60;
		var header = document.querySelector('header') ||
		             document.querySelector('.header') ||
		             document.querySelector('.site-header') ||
		             document.querySelector('.navbar') ||
		             document.querySelector('.nav') ||
		             document.querySelector('[class*="header"]') ||
		             document.querySelector('[class*="Header"]') ||
		             document.querySelector('[id*="header"]') ||
		             document.querySelector('[id*="Header"]');

		if (header) {
			var headerRect = header.getBoundingClientRect();
			var headerStyle = window.getComputedStyle(header);
			var headerPosition = headerStyle.position;

			if (headerPosition === 'fixed' || headerPosition === 'sticky') {
				headerOffset = headerRect.height + baseOffset;
				console.log('MyGarageEmbed: Detected fixed/sticky header height:', headerRect.height + 'px, total offset:', headerOffset + 'px');
			} else {
				var containerRect = container.getBoundingClientRect();
				var containerTop = containerRect.top;

				if (containerTop >= headerRect.bottom) {
					headerOffset = baseOffset;
					console.log('MyGarageEmbed: Container already below header, adding base offset:', headerOffset + 'px');
				} else {
					headerOffset = headerRect.height + baseOffset;
					console.log('MyGarageEmbed: Adding header height + base offset:', headerOffset + 'px');
				}
			}
		} else {
			headerOffset = baseOffset;
			console.log('MyGarageEmbed: No header found, using base offset:', headerOffset + 'px');
		}

		// Add margin-top to push content below the header
		var containerStyles = 'position: relative; min-height: 600px; z-index: 1;';
		containerStyles += ' margin-top: ' + headerOffset + 'px;';
		containerStyles += ' scroll-margin-top: ' + headerOffset + 'px;';
		containerStyles += ' padding-top: 40px;';

		container.style.cssText = (container.style.cssText || '') + containerStyles;

		// Create root container for React app
		_rootContainer = document.getElementById('root');
		if (!_rootContainer) {
			_rootContainer = document.createElement('div');
			_rootContainer.id = 'root';
			_rootContainer.style.cssText = 'min-height: 600px; position: relative;';
			container.appendChild(_rootContainer);
		}

		// Show loading indicator
		Render.ShowLoader(container);

		// Create hidden iframe for API communication
		Render.CreateCommunicationIframe();

		// Get app base URL from the bundle's shared config (not currentScript —
		// there is only one storefront <script> tag now).
		_appBaseUrl = getAppBaseUrl();
		console.log('MyGarageEmbed: App base URL:', _appBaseUrl);

		// Resolve the API key from shared config (data-api-key / ?apiKey= on the
		// storefront tag → window globals → iframe-key fallback) and publish the
		// globals the React app reads.
		var apiKey = getApiKey();
		if (apiKey) {
			window.__PARTS_FINDER_API_KEY__ = apiKey;
			console.log('MyGarageEmbed: API key set from shared config');
		}

		// Set app base URL for embedded mode
		window.__PARTS_FINDER_APP_BASE_URL__ = _appBaseUrl;

		// Load the app assets
		Render.LoadAssets();
	},

	ShowLoader: function (container) {
		var loader = document.createElement('div');
		loader.id = _loaderId;
		loader.style.cssText = 'display: flex; align-items: center; justify-content: center; min-height: 400px; flex-direction: column; gap: 16px;';
		loader.innerHTML = `
			<div style="border: 4px solid #f3f3f3; border-top: 4px solid #d9272e; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite;"></div>
			<p style="color: #6c757d; margin: 0; font-size: 14px;">Loading My Garage...</p>
			<style>
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			</style>
		`;

		if (_rootContainer) {
			_rootContainer.appendChild(loader);
		} else {
			container.appendChild(loader);
		}

		// Set timeout to show error message if loading takes too long
		setTimeout(function () {
			var loaderEl = document.getElementById(_loaderId);
			if (loaderEl && loaderEl.parentNode) {
				loaderEl.innerHTML = `
					<div style="border: 4px solid #f3f3f3; border-top: 4px solid #d9272e; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite;"></div>
					<p style="color: #dc3545; margin: 0; font-size: 14px;">Taking longer than expected...</p>
					<p style="color: #6c757d; margin: 8px 0 0 0; font-size: 0.875rem;">Please wait or refresh the page</p>
				`;
			}
		}, 10000);
	},

	HideLoader: function () {
		var loader = document.getElementById(_loaderId);
		if (loader && loader.parentNode) {
			loader.parentNode.removeChild(loader);
		}
	},

	CreateCommunicationIframe: function () {
		// Check if iframe already exists (can be shared with parts-finder)
		var existingIframe = document.getElementById('parts-search-iframe') ||
		                     document.getElementById('parts-finder-comm-iframe');

		if (existingIframe) {
			console.log('MyGarageEmbed: Reusing existing iframe');
			_communicationIframe = existingIframe;
			existingIframe.setAttribute('data-my-garage-comm', '');

			// Wait for iframe to be ready
			setTimeout(function() {
				_iframeReady = true;
				_iframeReadyCallbacks.forEach(function(callback) { callback(); });
				_iframeReadyCallbacks = [];
			}, 100);
			return;
		}

		// Create hidden iframe for communication
		var iframeSrc = _appBaseUrl + 'my-garage-iframe.html';
		var iframe = document.createElement('iframe');
		iframe.setAttribute('data-my-garage-comm', '');
		iframe.id = 'my-garage-comm-iframe';
		iframe.height = '1';
		iframe.width = '1';
		iframe.style.cssText = 'border:0; position:absolute; left:-9999px; top:-9999px; visibility:hidden;';
		iframe.src = iframeSrc;
		iframe.setAttribute('aria-hidden', 'true');
		iframe.setAttribute('tabindex', '-1');

		var body = document.body || document.getElementsByTagName('body')[0];
		body.appendChild(iframe);
		_communicationIframe = iframe;

		// Wait for iframe to load
		iframe.onload = function() {
			console.log('MyGarageEmbed: Communication iframe loaded');
			setTimeout(function() {
				_iframeReady = true;
				_iframeReadyCallbacks.forEach(function(callback) { callback(); });
				_iframeReadyCallbacks = [];
			}, 500);
		};

		iframe.onerror = function() {
			console.error('MyGarageEmbed: Communication iframe failed to load');
		};

		// Set up message listener
		window.addEventListener('message', Render.HandleIframeMessage);

		console.log('MyGarageEmbed: Created communication iframe');
	},

	HandleIframeMessage: function(event) {
		// Only accept messages from our app origin
		var appOrigin = _appBaseUrl.replace(/\/$/, '');
		if (event.origin !== appOrigin && !appOrigin.includes(event.origin)) {
			return;
		}

		var message = event.data;
		if (message && message.type === 'ASSET_URLS_RESPONSE') {
			if (_assetUrlsPromise) {
				_assetUrlsPromise.resolve(message.data);
				_assetUrlsPromise = null;
			}
		} else if (message && message.type === 'IFRAME_READY') {
			console.log('MyGarageEmbed: Iframe ready message received');
		}
	},

	WaitForIframeReady: function() {
		return new Promise(function(resolve) {
			if (_iframeReady && _communicationIframe && _communicationIframe.contentWindow) {
				resolve();
			} else {
				_iframeReadyCallbacks.push(resolve);
			}
		});
	},

	GetAssetUrlsFromIframe: function() {
		return new Promise(function(resolve, reject) {
			_assetUrlsPromise = { resolve: resolve, reject: reject };

			// Request asset URLs from iframe
			if (_communicationIframe && _communicationIframe.contentWindow) {
				_communicationIframe.contentWindow.postMessage({
					type: 'GET_ASSET_URLS'
				}, '*');

				// Timeout after 10 seconds
				setTimeout(function() {
					if (_assetUrlsPromise) {
						_assetUrlsPromise.reject(new Error('Timeout getting asset URLs'));
						_assetUrlsPromise = null;
					}
				}, 10000);
			} else {
				reject(new Error('Iframe not ready'));
			}
		});
	},

	LoadAssets: function () {
		if (_assetsLoaded) {
			console.log('MyGarageEmbed: Assets already loaded');
			return;
		}

		console.log('MyGarageEmbed: Loading assets via iframe...');

		// Wait for iframe to be ready, then request asset URLs
		Render.WaitForIframeReady()
			.then(function() {
				return Render.GetAssetUrlsFromIframe();
			})
			.then(function(assetData) {
				console.log('MyGarageEmbed: Received asset URLs:', assetData);

				var cssLinks = assetData.css || [];
				var jsScripts = assetData.js || [];

				// Load CSS files
				cssLinks.forEach(function(cssPath) {
					var fullUrl = cssPath.startsWith('http') ? cssPath : _appBaseUrl + cssPath.replace(/^\//, '');
					var link = document.createElement('link');
					link.rel = 'stylesheet';
					link.href = fullUrl;
					document.head.appendChild(link);
					console.log('MyGarageEmbed: Loaded CSS:', fullUrl);
				});

				// Load JS files sequentially
				var loadScript = function(index) {
					if (index >= jsScripts.length) {
						console.log('MyGarageEmbed: All scripts loaded');
						_assetsLoaded = true;
						Render.InitializeRouter();
						return;
					}

					var jsPath = jsScripts[index];
					var fullUrl = jsPath.startsWith('http') ? jsPath : _appBaseUrl + jsPath.replace(/^\//, '');
					var script = document.createElement('script');
					script.type = 'module';
					script.src = fullUrl;
					script.onload = function() {
						console.log('MyGarageEmbed: Loaded JS:', fullUrl);
						loadScript(index + 1);
					};
					script.onerror = function() {
						console.error('MyGarageEmbed: Failed to load JS:', fullUrl);
						loadScript(index + 1);
					};
					document.body.appendChild(script);
				};

				loadScript(0);
			})
			.catch(function(error) {
				console.error('MyGarageEmbed: Failed to load assets:', error);
				Render.HideLoader();

				// Show error message
				if (_rootContainer) {
					_rootContainer.innerHTML =
						'<div style="padding: 40px; text-align: center;">' +
						'<p style="color: #dc3545; font-size: 16px; margin-bottom: 10px;">Failed to load My Garage</p>' +
						'<p style="color: #6c757d; font-size: 14px; margin-bottom: 20px;">Please try refreshing the page</p>' +
						'<button onclick="window.location.reload()" style="padding: 10px 20px; background: #d9272e; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">Refresh Page</button>' +
						'</div>';
				}
			});
	},

	InitializeRouter: function () {
		console.log('MyGarageEmbed: Initializing router to /my-garage route');

		// Wait for React to mount, then navigate
		var checkInterval = setInterval(function () {
			// Check if React root is mounted by looking for React-specific elements
			var reactRoot = document.querySelector('#root > div');
			if (reactRoot) {
				console.log('MyGarageEmbed: React app mounted, navigating to /my-garage');
				clearInterval(checkInterval);

				// Hide loader
				Render.HideLoader();

				// Navigate to My Garage route
				// The React Router in the app will handle this
				if (window.history && window.history.pushState) {
					window.history.pushState({}, '', '/my-garage');

					// Dispatch popstate event to trigger React Router navigation
					var event = new PopStateEvent('popstate', { state: {} });
					window.dispatchEvent(event);
				}
			}
		}, 100);

		// Timeout after 15 seconds
		setTimeout(function () {
			clearInterval(checkInterval);
			if (document.getElementById(_loaderId)) {
				console.error('MyGarageEmbed: Timed out waiting for React app to mount');
				Render.HideLoader();
			}
		}, 15000);
	}
};

export function init() {
	// Per-instance target: the container carrying [data-parts-garage]. No-op on any
	// page that doesn't have it (this widget only lives on the My Garage page).
	var container = document.querySelector('[data-parts-garage]');
	if (!container) return;

	// Preserve the legacy 100ms defer so the storefront's own header/menu scripts
	// have settled before we measure the header offset and mount.
	setTimeout(function () {
		Render.Initialize(container);
	}, 100);
}

export var id = 'my-garage';
